import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import api from '../../../utils/api';
import 'react-datepicker/dist/react-datepicker.css';
import './Users.css';
import '../CleanerDashboard/AvailableTasks.css';

const Users = () => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [admins, setAdmins] = useState([]);

  // Helper function to get currency symbol
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'DKK': 'kr ',
      'SEK': 'kr ',
      'NOK': 'kr '
    };
    return symbols[currency] || '';
  };
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registerType, setRegisterType] = useState('client');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    clientType: 'private',
    companyName: '',
    vatNumber: '',
    experience: 0,
    adminNotes: '',
    hourlyPrice: '',
    currency: 'USD'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    clientType: 'private',
    companyName: '',
    vatNumber: '',
    adminNotes: '',
    hourlyPrice: '',
    currency: 'USD'
  });
  const [registerError, setRegisterError] = useState('');
  const [cleanerDocuments, setCleanerDocuments] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, [startDate, endDate, searchQuery]);

  useEffect(() => {
    fetchAdmins();
  }, [startDate, endDate, searchQuery]);

  const fetchUsers = async () => {
    try {
      const params = {};
      
      // Add date filters
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (searchQuery) params.search = searchQuery;
      
      // Fetch clients
      const clientsResponse = await api.get('/users', { params: { ...params, role: 'client' } });
      const sortedClients = (clientsResponse.data.data || []).sort((a, b) => 
        new Date(b.registeredAt || b.createdAt) - new Date(a.registeredAt || a.createdAt)
      );
      setClients(sortedClients);
      
      // Fetch cleaners
      const cleanersResponse = await api.get('/users', { params: { ...params, role: 'cleaner' } });
      const sortedCleaners = (cleanersResponse.data.data || []).sort((a, b) => 
        new Date(b.registeredAt || b.createdAt) - new Date(a.registeredAt || a.createdAt)
      );
      setCleaners(sortedCleaners);
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast(error.response?.data?.message || t('admin.errorFetchingUsers', 'Error fetching users. Please try again.'), 'error');
    }
  };

  const fetchAdmins = async () => {
    try {
      const params = {};
      
      // Add date filters
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (searchQuery) params.search = searchQuery;
      
      // Fetch admins
      const adminsResponse = await api.get('/users', { params: { ...params, role: 'admin' } });
      const sortedAdmins = (adminsResponse.data.data || []).sort((a, b) => 
        new Date(b.registeredAt || b.createdAt) - new Date(a.registeredAt || a.createdAt)
      );
      setAdmins(sortedAdmins);
    } catch (error) {
      console.error('Error fetching admins:', error);
      showToast(error.response?.data?.message || t('admin.errorFetchingAdmins', 'Error fetching admins. Please try again.'), 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`);
      setSelectedUser(response.data.data);
      setEditMode(false);
    } catch (error) {
      console.error('Error fetching user details:', error);
      showToast(error.response?.data?.message || t('admin.errorFetchingUser', 'Error fetching user details. Please try again.'), 'error');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditMode(true);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      password: '', // Don't pre-fill password
      phone: user.phone || '',
      address: user.address || '',
      clientType: user.clientType || 'private',
      companyName: user.companyName || '',
      vatNumber: user.vatNumber || '',
      experience: user.experience || 0,
      adminNotes: user.adminNotes || '',
      hourlyPrice: user.hourlyPrice || '',
      currency: user.currency || 'USD'
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!editFormData.name || !editFormData.email || !editFormData.phone || !editFormData.address) {
      showToast(t('admin.fillAllFields', 'Please fill in all required fields.'), 'warning');
      return;
    }

    if (selectedUser.role === 'client' && editFormData.clientType === 'company') {
      if (!editFormData.companyName || !editFormData.vatNumber) {
        showToast(t('admin.companyNameRequired', 'Company name and VAT number are required for company clients.'), 'warning');
        return;
      }
    }

    try {
      const userId = selectedUser.id || selectedUser._id;
      const updateData = {
        name: editFormData.name,
        email: editFormData.email,
        phone: editFormData.phone,
        address: editFormData.address,
        adminNotes: editFormData.adminNotes || '',
        ...(editFormData.password && { password: editFormData.password }),
        ...(selectedUser.role === 'client' && {
          clientType: editFormData.clientType,
          hourlyPrice: editFormData.hourlyPrice ? parseFloat(editFormData.hourlyPrice) : null,
          currency: editFormData.currency || 'USD',
          ...(editFormData.clientType === 'company' && {
            companyName: editFormData.companyName,
            vatNumber: editFormData.vatNumber
          })
        }),
        ...(selectedUser.role === 'cleaner' && {
          experience: parseInt(editFormData.experience) || 0,
          hourlyPrice: editFormData.hourlyPrice ? parseFloat(editFormData.hourlyPrice) : null,
          currency: editFormData.currency || 'USD'
        })
      };

      await api.put(`/users/${userId}`, updateData);
      
      showToast(t('admin.userUpdated', 'User updated successfully!'), 'success');
      setEditMode(false);
      setSelectedUser(null);
      fetchUsers();
      fetchAdmins();
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorUpdating', 'Error updating user. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleDeleteUser = async (userId, userType) => {
    if (!window.confirm(t('admin.confirmDelete', 'Are you sure you want to delete this user? This action cannot be undone.'))) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      
      showToast(t('admin.userDeleted', 'User deleted successfully!'), 'success');
      setSelectedUser(null);
      fetchUsers();
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorDeleting', 'Error deleting user. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.password || !formData.phone || !formData.address) {
      setRegisterError(t('admin.fillAllFields', 'Please fill in all required fields.'));
      return;
    }

    if (registerType === 'client' && formData.clientType === 'company') {
      if (!formData.companyName || !formData.vatNumber) {
        setRegisterError(t('admin.companyNameRequired', 'Company name and VAT number are required for company clients.'));
        return;
      }
    }

    try {
      const registerData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        address: formData.address,
        role: registerType,
        ...(registerType === 'client' && {
          clientType: formData.clientType,
          hourlyPrice: formData.hourlyPrice ? parseFloat(formData.hourlyPrice) : null,
          currency: formData.currency || 'USD',
          ...(formData.clientType === 'company' && {
            companyName: formData.companyName,
            vatNumber: formData.vatNumber
          })
        }),
        ...(registerType === 'cleaner' && {
          experience: 0, // Default experience for new cleaners
          hourlyPrice: formData.hourlyPrice ? parseFloat(formData.hourlyPrice) : null,
          currency: formData.currency || 'USD'
        })
      };

      // Register user via API
      const registerResponse = await api.post('/auth/register', registerData);
      const newUserId = registerResponse.data.data.user.id;

      if (formData.adminNotes) {
        await api.put(`/users/${newUserId}`, { adminNotes: formData.adminNotes });
      }

      if (registerType === 'cleaner' && cleanerDocuments.length > 0) {
        const formDataUpload = new FormData();
        cleanerDocuments.forEach((file) => formDataUpload.append('documents', file));
        await api.post(`/users/${newUserId}/upload-documents`, formDataUpload);
      }

      const roleName = registerType === 'client' ? t('admin.clientRegistered', 'Client') : 
                       registerType === 'cleaner' ? t('admin.cleanerRegistered', 'Cleaner') : 
                       t('admin.adminRegistered', 'Admin');
      showToast(`${roleName} ${t('admin.registeredSuccessfully', 'registered successfully!')}`, 'success');
      setShowRegisterForm(false);
      setRegisterError('');
      setCleanerDocuments([]);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        address: '',
        clientType: 'private',
        companyName: '',
        vatNumber: '',
        adminNotes: '',
        hourlyPrice: '',
        currency: 'USD'
      });
      fetchUsers();
      fetchAdmins();
    } catch (error) {
      console.error('Error registering user:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorRegistering', 'Error registering user. Please try again.');
      setRegisterError(errorMessage);
    }
  };

  return (
    <div className="users-management">
      <div className="users-header">
        <h2>{t('admin.usersManagement', 'Users Management')}</h2>
        <div className="filters-container">
          <div className="filter-group">
            <label>{t('common.search', 'Search')}:</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.searchPlaceholder', 'Search by name, email, or phone...')}
              className="filter-select"
            />
          </div>
          <div className="date-filter">
            <div className="date-filter-group">
              <label>{t('common.from', 'From')}:</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText={t('common.startDate', 'Start date')}
                dateFormat="dd,MM,yyyy"
                className="date-picker-input"
              />
            </div>
            <div className="date-filter-group">
              <label>{t('common.to', 'To')}:</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText={t('common.endDate', 'End date')}
                dateFormat="dd,MM,yyyy"
                className="date-picker-input"
              />
            </div>
            {(startDate || endDate || searchQuery) && (
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setSearchQuery('');
                }}
              >
                {t('common.clearFilter', 'Clear Filter')}
              </button>
            )}
          </div>
          <button 
            className="btn btn-primary"
            style={{ marginTop: 'auto' }}
            onClick={() => {
              setShowRegisterForm(true);
              setRegisterError('');
            }}
          >
            {t('admin.registerNewUser', 'Register New User')}
          </button>
        </div>
      </div>

      <div className="users-tabs">
        <button 
          className={activeTab === 'clients' ? 'active' : ''}
          onClick={() => setActiveTab('clients')}
        >
          {t('admin.clients', 'Clients')} ({clients.length})
        </button>
        <button 
          className={activeTab === 'cleaners' ? 'active' : ''}
          onClick={() => setActiveTab('cleaners')}
        >
          {t('admin.cleaners', 'Cleaners')} ({cleaners.length})
        </button>
        <button 
          className={activeTab === 'admins' ? 'active' : ''}
          onClick={() => setActiveTab('admins')}
        >
          {t('admin.admins', 'Admins')} ({admins.length})
        </button>
      </div>

      <div className="users-content">
        {activeTab === 'clients' && (
          <div className="users-list">
            <h3>{t('admin.allClients', 'All Clients')}</h3>
            {clients.length === 0 ? (
              <p className="no-users">{t('admin.noClients', 'No clients registered yet.')}</p>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="users-table-wrapper">
                  <table className="users-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('admin.name', 'Name')}</th>
                      <th>{t('auth.email', 'Email')}</th>
                      <th>{t('auth.phone', 'Phone')}</th>
                      <th>{t('admin.type', 'Type')}</th>
                      <th>{t('admin.registered', 'Registered')}</th>
                      <th>{t('admin.action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client, index) => (
                      <tr key={client.id || client._id}>
                        <td>{index + 1}</td>
                        <td>{client.name}</td>
                        <td>{client.email}</td>
                        <td>{client.phone}</td>
                        <td>{client.clientType || 'private'}</td>
                        <td>{new Date(client.registeredAt).toLocaleDateString('en-GB')}</td>
                        <td>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleViewUser(client._id || client.id)}
                          >
                            {t('admin.details', 'Details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                {/* Mobile Card View */}
                <div className="users-cards">
                  {clients.map((client, index) => (
                    <div key={client.id || client._id} className="user-card">
                      <div className="user-card-header">
                        <h4>#{index + 1} {client.name}</h4>
                        <span className="user-type-badge">{client.clientType || 'private'}</span>
                      </div>
                      <div className="user-card-details">
                        <div className="user-card-row">
                          <span className="user-card-label">{t('auth.email', 'Email')}</span>
                          <span className="user-card-value">{client.email}</span>
                        </div>
                        <div className="user-card-row">
                          <span className="user-card-label">{t('auth.phone', 'Phone')}</span>
                          <span className="user-card-value">{client.phone}</span>
                        </div>
                        <div className="user-card-row">
                          <span className="user-card-label">{t('admin.registered', 'Registered')}</span>
                          <span className="user-card-value">{new Date(client.registeredAt).toLocaleDateString('en-GB')}</span>
                        </div>
                      </div>
                      <div className="user-card-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleViewUser(client._id || client.id)}
                        >
                          {t('admin.details', 'Details')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'cleaners' && (
          <div className="users-list">
            <h3>{t('admin.allCleaners', 'All Cleaners')}</h3>
            {cleaners.length === 0 ? (
              <p className="no-users">{t('admin.noCleaners', 'No cleaners registered yet.')}</p>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="users-table-wrapper">
                  <table className="users-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('admin.name', 'Name')}</th>
                      <th>{t('auth.email', 'Email')}</th>
                      <th>{t('auth.phone', 'Phone')}</th>
                      <th>{t('admin.experience', 'Experience')}</th>
                      <th>{t('admin.action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleaners.map((cleaner, index) => {
                      const cleanerId = cleaner.id || cleaner._id;
                      return (
                        <tr key={cleanerId}>
                          <td>{index + 1}</td>
                          <td>{cleaner.name}</td>
                          <td>{cleaner.email}</td>
                          <td>{cleaner.phone || '-'}</td>
                          <td>{cleaner.experience || 0} years</td>
                          <td>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleViewUser(cleaner._id || cleaner.id)}
                            >
                              {t('admin.details', 'Details')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                {/* Mobile Card View */}
                <div className="users-cards">
                  {cleaners.map((cleaner, index) => {
                    const cleanerId = cleaner.id || cleaner._id;
                    return (
                      <div key={cleanerId} className="user-card">
                        <div className="user-card-header">
                          <h4>#{index + 1} {cleaner.name}</h4>
                          <span className="user-type-badge cleaner">Cleaner</span>
                        </div>
                        <div className="user-card-details">
                          <div className="user-card-row">
                            <span className="user-card-label">{t('auth.email', 'Email')}</span>
                            <span className="user-card-value">{cleaner.email}</span>
                          </div>
                          <div className="user-card-row">
                            <span className="user-card-label">{t('auth.phone', 'Phone')}</span>
                            <span className="user-card-value">{cleaner.phone || '-'}</span>
                          </div>
                          <div className="user-card-row">
                            <span className="user-card-label">{t('admin.experience', 'Experience')}</span>
                            <span className="user-card-value">{cleaner.experience || 0} years</span>
                          </div>
                        </div>
                        <div className="user-card-actions">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleViewUser(cleaner._id || cleaner.id)}
                          >
                            {t('admin.details', 'Details')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="users-list">
            <h3>{t('admin.allAdmins', 'All Admins')}</h3>
            {admins.length === 0 ? (
              <p className="no-users">{t('admin.noAdmins', 'No admins registered yet.')}</p>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="users-table-wrapper">
                  <table className="users-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('admin.name', 'Name')}</th>
                      <th>{t('auth.email', 'Email')}</th>
                      <th>{t('auth.phone', 'Phone')}</th>
                      <th>{t('admin.registered', 'Registered')}</th>
                      <th>{t('admin.action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((admin, index) => (
                      <tr key={admin.id}>
                        <td>{index + 1}</td>
                        <td>{admin.name}</td>
                        <td>{admin.email}</td>
                        <td>{admin.phone || '-'}</td>
                        <td>
                          {admin.registeredAt 
                            ? new Date(admin.registeredAt).toLocaleDateString('en-GB') 
                            : '-'}
                        </td>
                        <td>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleViewUser(admin._id || admin.id)}
                          >
                            {t('admin.details', 'Details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                {/* Mobile Card View */}
                <div className="users-cards">
                  {admins.map((admin, index) => (
                    <div key={admin.id} className="user-card">
                      <div className="user-card-header">
                        <h4>#{index + 1} {admin.name}</h4>
                        <span className="user-type-badge admin">Admin</span>
                      </div>
                      <div className="user-card-details">
                        <div className="user-card-row">
                          <span className="user-card-label">{t('auth.email', 'Email')}</span>
                          <span className="user-card-value">{admin.email}</span>
                        </div>
                        <div className="user-card-row">
                          <span className="user-card-label">{t('auth.phone', 'Phone')}</span>
                          <span className="user-card-value">{admin.phone || '-'}</span>
                        </div>
                        <div className="user-card-row">
                          <span className="user-card-label">{t('admin.registered', 'Registered')}</span>
                          <span className="user-card-value">
                            {admin.registeredAt 
                              ? new Date(admin.registeredAt).toLocaleDateString('en-GB') 
                              : '-'}
                          </span>
                        </div>
                      </div>
                      <div className="user-card-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleViewUser(admin._id || admin.id)}
                        >
                          {t('admin.details', 'Details')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showRegisterForm && (
        <div className="modal-overlay" onClick={() => {
          setShowRegisterForm(false);
          setRegisterError('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('admin.registerNewUser', 'Register New User')}</h3>
              <button className="close-btn" onClick={() => {
                setShowRegisterForm(false);
                setRegisterError('');
                setCleanerDocuments([]);
              }}>×</button>
            </div>
            <form onSubmit={handleRegister} className="register-form">
              <div className="form-group">
                <label>{t('admin.userTypeLabel', 'User Type *')}</label>
                <select
                  name="registerType"
                  value={registerType}
                  onChange={(e) => {
                    setRegisterType(e.target.value);
                    setCleanerDocuments([]);
                    setFormData({
                      ...formData,
                      clientType: 'private',
                      companyName: '',
                      vatNumber: ''
                    });
                  }}
                  required
                >
                  <option value="client">{t('auth.client', 'Client')}</option>
                  <option value="cleaner">{t('auth.cleaner', 'Cleaner')}</option>
                  <option value="admin">{t('admin.admin', 'Admin')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('admin.fullName', 'Full Name *')}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.email', 'Email *')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.password', 'Password *')}</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="form-group">
                <label>{t('admin.phone', 'Phone *')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.address', 'Address *')}</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {registerType === 'cleaner' && (
                <div className="form-group">
                  <label>{t('admin.attachDocuments', 'Attach Documents (CV, certificates, etc.)')}</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    multiple
                    onChange={(e) => setCleanerDocuments(Array.from(e.target.files || []))}
                    className="form-control"
                  />
                  {cleanerDocuments.length > 0 && (
                    <span className="documents-hint">{cleanerDocuments.length} file(s) selected</span>
                  )}
                </div>
              )}

              {(registerType === 'client' || registerType === 'cleaner') && (
                <div className="form-group">
                  <label>{t('profile.hourlyPrice', 'Hourly Price')}</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      style={{ width: '100px', padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', fontSize: '14px' }}
                    >
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="DKK">DKK (kr)</option>
                      <option value="SEK">SEK (kr)</option>
                      <option value="NOK">NOK (kr)</option>
                    </select>
                    <input
                      type="number"
                      name="hourlyPrice"
                      value={formData.hourlyPrice}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder={t('profile.hourlyPricePlaceholder', 'Enter hourly rate')}
                      style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>
                  {registerType === 'client' 
                    ? t('admin.clientNotes', 'Notes / Comments')
                    : t('admin.employeeNotes', 'Employee Notes / Comments')}
                </label>
                <textarea
                  name="adminNotes"
                  value={formData.adminNotes}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder={
                    registerType === 'client'
                      ? t('admin.clientNotesPlaceholder', 'Add any notes or comments about this client...')
                      : t('admin.employeeNotesPlaceholder', 'Add any notes or comments about this employee...')
                  }
                  className="form-textarea"
                />
              </div>

              {registerType === 'client' && (
                <>
                  <div className="form-group">
                    <label>{t('admin.clientTypeLabel', 'Client Type *')}</label>
                    <select
                      name="clientType"
                      value={formData.clientType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="private">{t('admin.private', 'Private')}</option>
                      <option value="company">{t('admin.company', 'Company')}</option>
                    </select>
                  </div>

                  {formData.clientType === 'company' && (
                    <>
                      <div className="form-group">
                        <label>{t('auth.companyName', 'Company Name *')}</label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          required={formData.clientType === 'company'}
                        />
                      </div>

                      <div className="form-group">
                        <label>{t('auth.vatNumber', 'VAT Number *')}</label>
                        <input
                          type="text"
                          name="vatNumber"
                          value={formData.vatNumber}
                          onChange={handleInputChange}
                          required={formData.clientType === 'company'}
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {registerError && (
                <div className="modal-error-message">{registerError}</div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">{t('admin.register', 'Register')}</button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRegisterForm(false);
                    setRegisterError('');
                  }}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="modal-overlay" onClick={() => {
          setSelectedUser(null);
          setEditMode(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? t('admin.editUser', 'Edit User') : t('admin.userDetails', 'User Details')}</h3>
              <button className="close-btn" onClick={() => {
                setSelectedUser(null);
                setEditMode(false);
              }}>×</button>
            </div>
            
            {editMode ? (
              <form onSubmit={handleUpdateUser} className="register-form">
                <div className="form-group">
                  <label>{t('admin.fullName', 'Full Name')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('auth.email', 'Email')} *</label>
                  <input
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('auth.password', 'Password')}</label>
                  <input
                    type="password"
                    name="password"
                    value={editFormData.password}
                    onChange={handleEditInputChange}
                    placeholder={t('admin.passwordPlaceholder', 'Leave blank to keep current password')}
                    minLength="6"
                  />
                </div>

                <div className="form-group">
                  <label>{t('auth.phone', 'Phone')} *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleEditInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('auth.address', 'Address')} *</label>
                  <input
                    type="text"
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditInputChange}
                    required
                  />
                </div>

                {selectedUser.role === 'client' && (
                  <>
                    <div className="form-group">
                      <label>{t('admin.clientTypeLabel', 'Client Type')} *</label>
                      <select
                        name="clientType"
                        value={editFormData.clientType}
                        onChange={handleEditInputChange}
                        required
                      >
                        <option value="private">{t('auth.private', 'Private')}</option>
                        <option value="company">{t('auth.company', 'Company')}</option>
                      </select>
                    </div>

                    {editFormData.clientType === 'company' && (
                      <>
                        <div className="form-group">
                          <label>{t('auth.companyName', 'Company Name')} *</label>
                          <input
                            type="text"
                            name="companyName"
                            value={editFormData.companyName}
                            onChange={handleEditInputChange}
                            required={editFormData.clientType === 'company'}
                          />
                        </div>

                        <div className="form-group">
                          <label>{t('auth.vatNumber', 'VAT Number')} *</label>
                          <input
                            type="text"
                            name="vatNumber"
                            value={editFormData.vatNumber}
                            onChange={handleEditInputChange}
                            required={editFormData.clientType === 'company'}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

                {selectedUser.role === 'cleaner' && (
                  <div className="form-group">
                    <label>{t('admin.experience', 'Experience (years)')}</label>
                    <input
                      type="number"
                      name="experience"
                      value={editFormData.experience}
                      onChange={handleEditInputChange}
                      min="0"
                    />
                  </div>
                )}

                {(selectedUser.role === 'client' || selectedUser.role === 'cleaner') && (
                  <div className="form-group">
                    <label>{t('profile.hourlyPrice', 'Hourly Price')}</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <select
                        name="currency"
                        value={editFormData.currency}
                        onChange={handleEditInputChange}
                        style={{ width: '100px', padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', fontSize: '14px' }}
                      >
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                        <option value="DKK">DKK (kr)</option>
                        <option value="SEK">SEK (kr)</option>
                        <option value="NOK">NOK (kr)</option>
                      </select>
                      <input
                        type="number"
                        name="hourlyPrice"
                        value={editFormData.hourlyPrice}
                        onChange={handleEditInputChange}
                        min="0"
                        step="0.01"
                        placeholder={t('profile.hourlyPricePlaceholder', 'Enter hourly rate')}
                        style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>{t('admin.employeeNotes', 'Employee Notes / Comments')}</label>
                  <textarea
                    name="adminNotes"
                    value={editFormData.adminNotes}
                    onChange={handleEditInputChange}
                    rows="4"
                    placeholder={t('admin.employeeNotesPlaceholder', 'Add any notes or comments about this employee...')}
                    className="form-textarea"
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {t('common.save', 'Save Changes')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditMode(false);
                      setSelectedUser(null);
                    }}
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                </div>
              </form>
            ) : (
              <div className="at-modal-body">
                {/* Role Badge */}
                <div className="at-detail-status">
                  <span className={`status status-${selectedUser.role}`} style={{ padding: '6px 18px', borderRadius: '20px', fontSize: '14px', fontWeight: 700, textTransform: 'capitalize', background: selectedUser.role === 'client' ? '#dbeafe' : selectedUser.role === 'cleaner' ? '#d1fae5' : '#fef3c7', color: selectedUser.role === 'client' ? '#1e40af' : selectedUser.role === 'cleaner' ? '#065f46' : '#92400e' }}>
                    {selectedUser.role === 'client' ? t('admin.userTypeClient', 'Client') : 
                     selectedUser.role === 'cleaner' ? t('admin.userTypeCleaner', 'Cleaner') : 
                     selectedUser.role === 'admin' ? t('admin.admin', 'Admin') : 'N/A'}
                  </span>
                </div>

                {/* Basic Information */}
                <div className="at-detail-section">
                  <h4>{t('admin.basicInformation', 'Basic Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🆔</span>
                    <span><strong>ID:</strong> {selectedUser.id || selectedUser._id || 'N/A'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">👤</span>
                    <span><strong>{t('admin.name', 'Name')}:</strong> {selectedUser.name}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📧</span>
                    <span><strong>{t('auth.email', 'Email')}:</strong> {selectedUser.email}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📞</span>
                    <span><strong>{t('auth.phone', 'Phone')}:</strong> {selectedUser.phone || 'N/A'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📍</span>
                    <span><strong>{t('auth.address', 'Address')}:</strong> {selectedUser.address || 'N/A'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🏙️</span>
                    <span><strong>{t('auth.city', 'City')}:</strong> {selectedUser.city || '-'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📮</span>
                    <span><strong>{t('auth.zipCode', 'Zip Code')}:</strong> {selectedUser.zipCode || '-'}</span>
                  </div>
                </div>

                {/* Client Details */}
                {selectedUser.role === 'client' && (
                  <div className="at-detail-section">
                    <h4>{t('admin.clientDetails', 'Client Details')}</h4>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">🏷️</span>
                      <span><strong>{t('admin.clientType', 'Client Type')}:</strong> {selectedUser.clientType || 'private'}</span>
                    </div>
                    {selectedUser.clientType === 'company' && (
                      <>
                        <div className="at-detail-row">
                          <span className="at-detail-icon">🏢</span>
                          <span><strong>{t('auth.companyName', 'Company Name')}:</strong> {selectedUser.companyName || 'N/A'}</span>
                        </div>
                        <div className="at-detail-row">
                          <span className="at-detail-icon">📋</span>
                          <span><strong>{t('auth.vatNumber', 'VAT Number')}:</strong> {selectedUser.vatNumber || 'N/A'}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Cleaner Details */}
                {selectedUser.role === 'cleaner' && (
                  <div className="at-detail-section">
                    <h4>{t('admin.cleanerDetails', 'Cleaner Details')}</h4>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">⭐</span>
                      <span><strong>{t('admin.experience', 'Experience')}:</strong> {selectedUser.experience || 0} {t('common.years', 'years')}</span>
                    </div>
                  </div>
                )}

                {/* Pricing */}
                {(selectedUser.role === 'client' || selectedUser.role === 'cleaner') && (
                  <div className="at-detail-section">
                    <h4>{t('admin.pricing', 'Pricing')}</h4>
                    <div className="at-detail-row">
                      <span className="at-detail-icon">💰</span>
                      <span><strong>{t('profile.hourlyPrice', 'Hourly Price')}:</strong> {selectedUser.hourlyPrice !== null && selectedUser.hourlyPrice !== undefined && selectedUser.hourlyPrice !== '' && !isNaN(selectedUser.hourlyPrice)
                        ? `${getCurrencySymbol(selectedUser.currency || 'USD')}${selectedUser.hourlyPrice} ${selectedUser.currency || 'USD'}`
                        : 'N/A'}</span>
                    </div>
                  </div>
                )}

                {/* Account Information */}
                <div className="at-detail-section">
                  <h4>{t('admin.accountInformation', 'Account Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📅</span>
                    <span><strong>{t('admin.registrationDate', 'Registration Date')}:</strong> {selectedUser.registeredAt 
                      ? new Date(selectedUser.registeredAt).toLocaleString() 
                      : 'N/A'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">✍️</span>
                    <span><strong>{t('admin.registeredBy', 'Registered By')}:</strong> {selectedUser.registeredBy || t('admin.selfRegistered', 'Self-registered')}</span>
                  </div>
                </div>

                {/* Admin Notes */}
                {selectedUser.adminNotes && (
                  <div className="at-detail-section">
                    <h4>{t('admin.employeeNotes', 'Employee Notes / Comments')}</h4>
                    <div className="at-detail-comments">{selectedUser.adminNotes}</div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="at-modal-actions" style={{ padding: '16px 0 0', borderTop: '1px solid #e2e8f0' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleEditUser(selectedUser)}
                  >
                    {t('common.edit', 'Edit')}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      handleDeleteUser(selectedUser.id || selectedUser._id, selectedUser.role);
                      setSelectedUser(null);
                    }}
                  >
                    {t('common.delete', 'Delete')}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedUser(null);
                      setEditMode(false);
                    }}
                  >
                    {t('common.close', 'Close')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
